import {
  Repository,
  Team,
  VercelDeployButtonConfig,
} from '@basehub/data/replicache/index'
import {
  PublicRepo,
  PublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { siteOrigin } from '~/constants/routing'
import { env as clientEnv } from '~/env.js'

export const getTemplateDeployUrl = ({
  repo,
  team,
  teamToDeployToSlug,
  visibility,
  repoName,
}: {
  repo: Repository | PublicRepo
  team: Team | PublicTeam
  teamToDeployToSlug?: string
  visibility?: 'public' | 'private'
  repoName?: string
}) => {
  if (!repo.gitRepoUrl) {
    return { enabled: false }
  }

  let enabled = true
  const errors: Partial<Record<keyof VercelDeployButtonConfig, string>> = {}

  try {
    new URL(repo.gitRepoUrl)
  } catch (_) {
    errors.gitRepoUrl = 'Invalid URL.'
    enabled = false
  }

  const demoUrl =
    repo.readme?.vercelDeployButton?.demoUrl ?? repo?.previewUrl ?? ''
  if (demoUrl) {
    try {
      new URL(demoUrl)
    } catch (_) {
      errors.demoUrl = 'Invalid URL.'
      enabled = false
    }
  }

  const envVars = repo.readme?.vercelDeployButton?.envVars ?? ''
  const env = envVars
    ?.split(',')
    .map((v) => v.trim())
    .filter((v) => !!v)
  if (env) {
    // can only have letters, numbers, and underscores. no special characters. must start with a letter.
    const envRegex = /^[a-zA-Z][a-zA-Z0-9_]*$/
    env.forEach((v) => {
      const valid = envRegex.test(v)
      if (!valid) {
        errors.envVars =
          'Environment Variable keys must start with a letter and use no special characters other than underscores ("_").'
        enabled = false
      }
    })
  }

  let deployURL = `https://vercel.com/new/clone?repository-url=${encodeURIComponent(
    repo.gitRepoUrl
  )}`

  deployURL += `&integration-ids=${clientEnv.NEXT_PUBLIC_VERCEL_INTEGRATION_CLIENT_ID}`
  deployURL += `&project-name=${repo.slug}&repository-name=${repo.slug}`
  deployURL += `&redirect-url=${encodeURIComponent(
    new URL('/api/vercel/redirect-repo', siteOrigin).toString()
  )}`
  if (env) {
    deployURL += `&env=${encodeURIComponent(env.join(','))}`
  }
  if (demoUrl && repo.description && repo.readme?.screenshots?.[0]?.url) {
    // add demo data if all values are present
    deployURL += `&demo-title=${encodeURIComponent(repo.title)}`
    deployURL += `&demo-url=${encodeURIComponent(demoUrl)}`
    deployURL += `&demo-description=${encodeURIComponent(repo.description)}`
    // we remove the demo-image for vercel issues displaying the image (they use contentful)
    // deployURL += `&demo-image=${encodeURIComponent(
    //   repo.readme.screenshots[0].url
    // )}`
  }

  const teamId = team.id
  const repoId = repo.id
  const tokenScope = repo.readme?.vercelDeployButton?.tokenScope ?? 'read'
  if (teamId && repoId) {
    const externalId = createExternalId({
      teamId,
      repoId,
      tokenScope,
      teamToDeployToSlug,
      visibility,
      repoName,
    })
    deployURL += `&external-id=${encodeURIComponent(externalId)}`
  } else return { errors, enabled: false }

  return { errors, enabled, deployURL: enabled ? deployURL : '#' }
}

const BASEHUB_NULL_VALUE = '<basehub-null-value>'

export const createExternalId = ({
  teamId,
  repoId,
  tokenScope,
  teamToDeployToSlug,
  visibility,
  repoName,
}: {
  teamId: string
  repoId: string
  tokenScope: string
  teamToDeployToSlug?: string
  visibility?: 'public' | 'private'
  repoName?: string
}) => {
  return `${teamId}:${repoId}:${tokenScope || 'read'}:${
    teamToDeployToSlug ? `${teamToDeployToSlug}` : BASEHUB_NULL_VALUE
  }:${repoName ? `${repoName}` : BASEHUB_NULL_VALUE}:${
    visibility ? `${visibility}` : BASEHUB_NULL_VALUE
  }`
}

export const parseExternalId = (externalId: string) => {
  const [teamId, repoId, tokenScope, teamToDeployToSlug, repoName, visibility] =
    externalId.split(':').map((v) => v.trim())

  // return null on the fields that value is null
  return {
    teamId,
    repoId,
    tokenScope,
    teamToDeployToSlug:
      teamToDeployToSlug === BASEHUB_NULL_VALUE
        ? undefined
        : teamToDeployToSlug,
    repoName: repoName === BASEHUB_NULL_VALUE ? undefined : repoName,
    visibility: visibility === BASEHUB_NULL_VALUE ? undefined : visibility,
  }
}
